import React from 'react';
import { ReferenceChip } from 'src/modules/shared/components/atoms/reference-chip';
import {
  tokenizeReferences,
  type ReferenceHighlighterOptions,
  type ReferenceToken,
} from 'src/modules/shared/utils/reference/reference-highlighter.util';
import type { FunnelReferenceMeta } from 'src/modules/settings/types/funnels/statuses/status-description.types';

type NodeLike = React.ReactNode;

interface StatusReferenceHighlighterProps {
  children: NodeLike;
  definitions: ReferenceHighlighterOptions<FunnelReferenceMeta>['definitions'];
}

const renderToken = (
  token: ReferenceToken<FunnelReferenceMeta>,
  index: number
): React.ReactNode => {
  if (token.kind === 'text') {
    return token.value;
  }

  const chipKind = token.kind === 'step' ? 'step' : 'status';
  const chipKey = `ref-${chipKind}-${index}`;

  return <ReferenceChip key={chipKey} kind={chipKind} label={token.value} />;
};

const renderTokens = (
  tokens: ReferenceToken<FunnelReferenceMeta>[]
): React.ReactNode[] => tokens.map((token, index) => renderToken(token, index));

const processNode = (
  node: NodeLike,
  options: ReferenceHighlighterOptions<FunnelReferenceMeta>
): NodeLike => {
  if (typeof node === 'string') {
    const tokens = tokenizeReferences<FunnelReferenceMeta>(node, options);
    return renderTokens(tokens);
  }

  if (Array.isArray(node)) {
    return node.map((child, idx) => (
      <React.Fragment key={idx}>{processNode(child, options)}</React.Fragment>
    ));
  }

  return node;
};

export const StatusReferenceHighlighter: React.FC<
  StatusReferenceHighlighterProps
> = ({ children, definitions }) => {
  if (!children || !definitions.length) {
    return <>{children}</>;
  }

  return <>{processNode(children, { definitions })}</>;
};
